
import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { X, MessageSquare, Send } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useToast } from '@/components/ui/use-toast';
import { sendEmail } from '@/utils/emailService';
import { useForm } from '@/hooks/useForm';

const FeedbackModal = ({ isOpen, onClose, itemTitle, onSubmit }) => {
  const { toast } = useToast();

  const validationRules = {
    feedback: {
      required: true,
      validator: (value) => value && value.length >= 10 && value.length <= 2000,
      requiredMessage: "Voer feedback in",
      message: "Feedback moet tussen 10 en 2000 karakters bevatten"
    }
  };

  const {
    formData,
    isSubmitting,
    errors,
    handleChange,
    handleSubmit,
    resetForm
  } = useForm({
    feedback: '',
    name: '',
    email: ''
  }, validationRules);

  const handleFeedbackSubmit = async (sanitizedData) => {
    try {
      // EmailJS template parameters
      const templateParams = {
        name: sanitizedData.name || 'Anoniem',
        email: sanitizedData.email || 'Niet opgegeven',
        message: sanitizedData.feedback,
        title: `Roadmap Feedback - ${itemTitle}`,
        time: new Date().toLocaleString('nl-NL')
      };
      
      const emailResult = await sendEmail(templateParams);
      
      if (emailResult.success) {
        // Call the original onSubmit for any additional handling
        onSubmit({
          feedback: sanitizedData.feedback,
          name: sanitizedData.name,
          email: sanitizedData.email,
          itemTitle
        });
        
        // Reset form and close modal
        resetForm();
        onClose();
        
        toast({
          title: "Je feedback is verzonden! ✅",
          description: "Bedankt voor je waardevolle feedback. Je input is automatisch naar ons gestuurd!",
          duration: 6000,
        });
        
        return { success: true };
      } else {
        throw new Error(emailResult.error || 'Feedback kon niet verzonden worden');
      }
    } catch (error) {
      console.error('Feedback submission error:', error);
      toast({
        title: "EmailJS configuratie vereist ⚙️",
        description: "Email service moet nog volledig geconfigureerd worden. Controleer de console voor setup instructies.",
        variant: "destructive",
        duration: 10000,
      });
      return { success: false };
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-md"
        >
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center">
                  <MessageSquare className="w-5 h-5 mr-2 text-cm-blue" />
                  Feedback geven
                </CardTitle>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleClose}
                  className="h-8 w-8"
                  disabled={isSubmitting}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-sm text-cm-gray">
                Geef feedback over: <span className="font-semibold">{itemTitle}</span>
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={(e) => {
                e.preventDefault();
                handleSubmit(handleFeedbackSubmit);
              }} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Naam (optioneel)</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Je naam"
                    disabled={isSubmitting}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email">Email (optioneel)</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="je@email.com"
                    disabled={isSubmitting}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="feedback">Jouw feedback *</Label>
                  <Textarea
                    id="feedback"
                    name="feedback"
                    value={formData.feedback}
                    onChange={handleChange}
                    placeholder="Deel je gedachten, suggesties of complimenten..."
                    className="min-h-[100px]"
                    required
                    disabled={isSubmitting}
                  />
                  {errors.feedback && <p className="text-red-500 text-sm mt-1">{errors.feedback}</p>}
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                    className="flex-1"
                    disabled={isSubmitting}
                  >
                    Annuleren
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-cm-blue hover:bg-cm-blue-dark disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={!formData.feedback.trim() || isSubmitting}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {isSubmitting ? 'Verzenden...' : 'Verzenden'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default FeedbackModal;
